import React from 'react';
import styled, { useTheme } from 'styled-components';
import { AbstractReactFactory, GenerateWidgetEvent } from '@gacha/gacha-diagrams/canvas';
import { DiagramEngine } from '@gacha/gacha-diagrams';
import { PortType, GenericNodeWidgetProps, NodeType } from '../util';
import NodeWidget from '../NodeWidget';
import { Theme } from '../../theme';
import { ConstrainedPortModel } from '../ports';
import { BalancerNodeModel } from './balancer';
import { DistNodeModel } from './distributor';
import { PERM_COPY, PERM_MODIFY, PERM_TRANSFER, StateModelOptions } from '.';
import { useNodeStateListeners } from '../util/hooks';
import { StateNodeModel } from '../StateNodeModel';
import { useStore } from '../../store';
import { NodeChildItem, NodeChildList } from '../styles';
import { normalize_key } from '../../util';

export interface VendorNodeModelOptions extends StateModelOptions { }

export class VendorNodeModel extends StateNodeModel {
  static NODE_TYPE = 'vendor-node';
  stateOptions: VendorNodeModelOptions;

  constructor(options: VendorNodeModelOptions) {
    super({
      ...options,
      type: VendorNodeModel.NODE_TYPE
    });
    this.stateOptions = options;

    this.addPort(
			new ConstrainedPortModel({
				in: true,
        name: PortType.Balancer,
        nodeTypeConstraint: BalancerNodeModel.NODE_TYPE,
        noDuplicateLinks: true
			})
		);
		this.addPort(
			new ConstrainedPortModel({
				in: true,
        name: PortType.Distributor,
        nodeTypeConstraint: DistNodeModel.NODE_TYPE,
        maximumLinks: 1,
        noDuplicateLinks: true
			})
    );
  }

  serialize() {
    return {
      ...super.serialize(),
      stateOptions: this.stateOptions
    }
  }

  deserialize(event: any): void {
    super.deserialize(event);
    this.stateOptions = event.data.options;
  }
}

export interface VendorNodeWidgetProps extends GenericNodeWidgetProps<VendorNodeModel> { }

export const VendorNodeWidget = ({ engine, node }: VendorNodeWidgetProps) => {
  const theme = useTheme() as Theme;
  const color = theme.colors.nodes[NodeType.Vendor].primary;

  useNodeStateListeners(node, engine, node.getStateId());

  const stateId = node.getStateId();
  const live_props = useStore(state => state.diagram.nodes[stateId]);
  
  const children = (<NodeChildList>
    {Object.entries(live_props.props.items ?? {}).map(item => (
      <NodeChildItem key={item[0]}>
        <VendorNameLabel>{normalize_key(item[0])}</VendorNameLabel>
        <span style={{fontFamily: "monospace", float: "right"}}>
          L${item[1].price || 0}&nbsp;
          {item[1].perm & PERM_COPY ? 'C' : '\u00a0'}
          {item[1].perm & PERM_MODIFY ? 'M' : '\u00a0'}
          {item[1].perm & PERM_TRANSFER ? 'T' : '\u00a0'}
        </span>
      </NodeChildItem>
    ))}
  </NodeChildList>);

  return (
    <>
      <NodeWidget selected={node.isSelected()} color={color} icon="gacha-node" node={node} engine={engine} children={children} />
    </>
  );
}

export class VendorNodeFactory extends AbstractReactFactory<VendorNodeModel, DiagramEngine> {
  constructor() {
    super(VendorNodeModel.NODE_TYPE);
  }

  generateModel({ initialConfig }: { initialConfig: any }) {
    return new VendorNodeModel({
      stateId: initialConfig.stateId
    } as any);
  }

  generateReactWidget(event: GenerateWidgetEvent<VendorNodeModel>) {
    return (
      <VendorNodeWidget engine={this.engine as DiagramEngine} node={event.model} />
    );
  }
}

const VendorNameLabel = styled.span`
  display: inline-block;
  width: 9.0em;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;